import { useCallback, useState, useRef, useMemo, useEffect } from 'react'
import { ILineItemWithOrder, ISessionWithBookings } from '../../interfaces/Session'
import Button from '../../components/Shared/Button';
import { useSession } from '../../components/Session/useSession';
import Modal from '../../components/Shared/Modal';
import ModalSection from '../../components/Modal/ModalSection';
import { EBattleKartGames } from '../../interfaces';
import { useDriverRegistration } from '../../context/DriverRegistration';
import TextInput from '../../components/Shared/TextInput';
import { IAddedPlayer, IBattleKartConfig, IRemovedPlayer } from '../../interfaces/battlekart';
import { BattleKartGameIconWithText, getConfigByOptions } from '../../utilities';
import { PlusIcon, XIcon, CheckIcon, EyeIcon, PlusSmIcon } from '@heroicons/react/outline';
import InputSlider from '../../components/Shared/InputSlider';
import { IFirebaseBKSession } from '../../interfaces/firebaseEntities';
import { IDriver } from '../../interfaces/BookingRegistration';
import { useBattleKart } from '../../context/BattleKart';


type TModalType = "config";

const StartBattleKartButton = ({ bookings, session, refreshBKData, bkSession, handleViewRegistrationModalOpen }: { bookings: ILineItemWithOrder[], session: ISessionWithBookings, refreshBKData: () => void, bkSession: IFirebaseBKSession, handleViewRegistrationModalOpen: (id: number) => void }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [showModal, setShowModal] = useState<TModalType | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortOrder, setSortOrder] = useState('');
    const [config, setConfig] = useState<IBattleKartConfig[]>([]);
    const [clearedDrivers, setClearedDrivers] = useState<IRemovedPlayer[]>([]);
    const [customPlayers, setCustomPlayers] = useState<IAddedPlayer[]>([]);
    const userSession = useSession();
    const trackStaff = useMemo(() => userSession.permissions?.roles?.includes("TRACK_STAFF"), [userSession]);
    
    const MAX_GAME_PER_SESSION = useMemo(() => trackStaff ? 2 : 4, [trackStaff]);
    
    const { bookings: registrationBookings, drivers } = useDriverRegistration();
    const { startGame, updateSession } = useBattleKart();

    // Add missing refs for drag and drop
    const dragItem = useRef<number | null>(null);
    const dragOverItem = useRef<number | null>(null);
    const durationTimeoutRefs = useRef<Record<number, NodeJS.Timeout>>({});


    const sku = useMemo(() => session.sku, [session]);
    const status = useMemo(() => bkSession?.status || "not_started", [bkSession]);
    const registeredDrivers = useMemo(() => bookings.map(b=>{
        const registrationBooking = registrationBookings[b.order.id.toString()];
        
        const driversData = registrationBooking?.registrations?.filter(r=>r.sku === session.sku).map(r => r.driverId)?.map(d=>drivers[d])?.filter(Boolean) || [];
        return driversData.map(d => ({
            orderId: b.order.id,
            driver: d
        })).flat();
    }).flat(), [bookings, registrationBookings, drivers, session]);

    const filteredRegisteredDrivers = useMemo(() => registeredDrivers.filter(d=>`${d?.driver?.first_name?.toLowerCase()} ${d?.driver?.last_name?.toLowerCase()}`.includes(searchQuery.toLowerCase())).sort((a, b) => {
        if (sortOrder === "asc") {
            return a.driver.first_name.localeCompare(b.driver.first_name);
        } else if (sortOrder === "desc") {
            return b.driver.first_name.localeCompare(a.driver.first_name);
        }
        return 0;
    }), [registeredDrivers, searchQuery, sortOrder]);

    const totalTime = useMemo(() => {
        return config.reduce((sum, mode) => sum + mode.duration, 0);
    }, [config]);

    const remainingTime = useMemo(() => Math.max(MAX_SESSION_TIME - totalTime, 0), [totalTime]);
    const totalNumOfDrivers = useMemo(() => bookings.reduce((sum, booking) => sum + (booking.current_quantity || 0), 0), [bookings]);

    const isDriverCleared = useCallback((orderId: number, driverId: string | undefined) => {
        if(!driverId) return false;
        return clearedDrivers.some(d => d.orderId === orderId && d.driverId === driverId);
    }, [clearedDrivers]);

    const totalNumOfDriversRegistered = useMemo(() => registeredDrivers.filter(d=>!isDriverCleared(d.orderId, d.driver.firebase_id)).length, [registeredDrivers, isDriverCleared]);

    const isConfirmButtonDisabled = useMemo(() => {
        return (totalTime <= 0) || (config.length === 0) || !registeredDrivers.filter(d=>!isDriverCleared(d.orderId, d.driver.firebase_id)).length || totalNumOfDriversRegistered > MAX_NUMBER_OF_PLAYERS;
    }, [totalTime, config, registeredDrivers, totalNumOfDriversRegistered, isDriverCleared]);

    const openConfigModal = useCallback(() => {
        setShowModal("config");
    }, [])

    const onSubmit = useCallback(async () => {
        try {
            const getConfirmMessage = () => {
                const action = status === "completed" ? "start a new game" : status === "in_progress" ? "restart it" : "start the game";
                
                const warnings = [];
                if (status === "in_progress") warnings.push("Game is already started");
                if (status === "completed") warnings.push("Game is already finished");
                if (totalTime < MAX_SESSION_TIME) warnings.push(`Total time is less than ${MAX_SESSION_TIME} minutes`);
                if (totalTime > MAX_SESSION_TIME) warnings.push(`Total time is more than ${MAX_SESSION_TIME} minutes`);
                if (config.length > MAX_GAME_PER_SESSION) warnings.push(`You have ${config.length} game modes in your session`);
                
                const warningText = warnings.length > 0 ? '\n' + warnings.map(w => `• ${w}`).join('\n') : "";
                
                return `Are you sure you want to ${action}?${warningText}`;
            };

            const hasWarnings = totalTime !== MAX_SESSION_TIME || config.length > MAX_GAME_PER_SESSION || status !== "not_started";
            if (hasWarnings && !window.confirm(getConfirmMessage())) return;
            
            const variantId = bookings && bookings.length && bookings[0]?.variant_id;
            if(!variantId){
                alert("No variant found");
                return;
            }
            setIsLoading(true);

            await startGame(variantId, config, clearedDrivers, customPlayers);
            alert("Game started successfully");

            refreshBKData();
            setIsLoading(false);
            setShowModal(null);
        } catch (error) {
            console.log(error);
            alert("Error starting Battle Kart Game. " + (error as any)?.response?.data?.message || (error as any)?.message);
            setIsLoading(false);
            setShowModal(null);
        }
    }, [bookings, config, clearedDrivers, totalTime, status, customPlayers, MAX_GAME_PER_SESSION, refreshBKData, startGame]);

    const handleAddMode = useCallback((mode: EBattleKartGames, duration: number) => {
        const newConfig = [...config, { name: mode, duration }];
        setConfig(newConfig);
        updateSession(sku, { config: newConfig });
    },[config, sku, updateSession]);

    // --- Drag and Drop Handlers for Game Modes ---

    const handleDragStart = (e: React.DragEvent<HTMLDivElement>, position: number) => {
        dragItem.current = position;
        e.currentTarget.classList.add(...draggingItemClasses.dragging);
    };

    const handleDragEnter = (e: React.DragEvent<HTMLDivElement>, position: number) => {
        dragOverItem.current = position;
        // Add visual feedback for drag-over, but only if it's a different item
        if (dragItem.current !== dragOverItem.current) {
            e.currentTarget.classList.add(...draggingItemClasses.hover);
            e.currentTarget.classList.remove(...draggingItemClasses.normal);
        }
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.currentTarget.classList.remove(...draggingItemClasses.hover);
        e.currentTarget.classList.add(...draggingItemClasses.normal);
    };

    const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.currentTarget.classList.remove(...draggingItemClasses.hover);
        e.currentTarget.classList.add(...draggingItemClasses.normal);

        const copyListItems = [...config];
        const dragItemContent = copyListItems[dragItem.current!];
        copyListItems.splice(dragItem.current!, 1);
        copyListItems.splice(dragOverItem.current!, 0, dragItemContent);
        dragItem.current = null;
        dragOverItem.current = null;
        setConfig(copyListItems);
        updateSession(sku, { config: copyListItems });
    },[config, sku, updateSession]);

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault(); // Necessary to allow dropping
        e.dataTransfer.dropEffect = 'move';
    };

    const handleDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
        e.currentTarget.classList.remove(...draggingItemClasses.dragging);
        // Clean up any remaining highlights from drag-over
        const items = document.querySelectorAll('#currentSelectedModesContainer > div');
        items.forEach((item: Element) => {
            item.classList.remove(...draggingItemClasses.hover);
            item.classList.add(...draggingItemClasses.normal);
        });
    };

    const handleRemoveSelectedMode = useCallback((indexToRemove: number) => {
        const newConfig = config.filter((_, index) => index !== indexToRemove)
        setConfig(newConfig);
        updateSession(sku, { config: newConfig });
    }, [config, sku, updateSession]);

    const handleSortDriversChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
        setSortOrder(e.target.value);
    }, []);

    const clearDriver = useCallback((orderId: number, driverId: string | undefined) => {
        if(!driverId) return;
        const newClearedDrivers = [...clearedDrivers, {orderId, driverId}];
        setClearedDrivers(newClearedDrivers);
        updateSession(sku, { removed_players: newClearedDrivers });
    }, [clearedDrivers, sku, updateSession]);

    const addDriver = useCallback((orderId: number, driverId: string | undefined) => {
        if(!driverId) return;
        const newClearedDrivers = clearedDrivers.filter(d => d.orderId !== orderId || d.driverId !== driverId);
        setClearedDrivers(newClearedDrivers);
        updateSession(sku, { removed_players: newClearedDrivers });
    }, [clearedDrivers, sku, updateSession]);

    // Update parent state changes
    useEffect(() => {
      setConfig(bkSession?.config || getConfigByOptions(session.option2 || "", session.option3 || ""));
      setClearedDrivers(bkSession?.removed_players || []);
      setCustomPlayers(bkSession?.added_players || []);
    }, [bkSession, session])

    const closeModal = useCallback(() => {
        // reset the states
        setShowModal(null);
        setSearchQuery("");
        setSortOrder("");
    }, []);

    const resetConfig = useCallback(() => {
        const newConfig = getConfigByOptions(session.option2 || "", session.option3 || "");
        setConfig(newConfig)
        updateSession(sku, { config: newConfig });
    }, [session, sku, updateSession]);

    const handleViewDriverClick = useCallback((driver: {orderId: number, driver: IDriver}) =>  () => {
        const booking = bookings.find(b => b.order.id === driver.orderId);
        if(booking){
            handleViewRegistrationModalOpen(booking.id);
            setShowModal(null);
        }
    }, [handleViewRegistrationModalOpen, bookings]);

    const handleChangeDuration = useCallback((index: number, duration: number) => {
        const newConfig = [...config];
        newConfig[index] && (newConfig[index].duration = duration);
        setConfig(newConfig);

        if (durationTimeoutRefs.current[index]) {
            clearTimeout(durationTimeoutRefs.current[index]);
        }
        
        durationTimeoutRefs.current[index] = setTimeout(() => {
            updateSession(sku, { config: newConfig });
            delete durationTimeoutRefs.current[index];
        }, 800);
    }, [config, sku, updateSession]);

    const handleAddCustomPlayer = useCallback((newPlayer: IAddedPlayer) => {
        const newCustomPlayers = [...customPlayers, newPlayer];
        setCustomPlayers(newCustomPlayers);
        updateSession(sku, { added_players: newCustomPlayers });
    }, [customPlayers, sku, updateSession]);

    const removeCustomPlayer = useCallback((index: number) => {
        const newCustomPlayers = customPlayers.filter((_, i) => i !== index);
        setCustomPlayers(newCustomPlayers);
        updateSession(sku, { added_players: newCustomPlayers });
    }, [customPlayers, sku, updateSession]);

    const buttonLabel = useMemo(() => {
        if(status === "in_progress") return "Started";
        if(status === "not_started") return "Start Game";
        if(status === "completed") return "Finished";
        return ""
    }, [status]);

    const butttonClass = useMemo(()=>{
        if(status === "in_progress") return "bg-yellow-500 hover:bg-yellow-600";
        if(status === "not_started") return "bg-track-battle/95 hover:bg-track-battle";
        if(status === "completed") return "bg-[#FF0000] hover:bg-[#FF0000]/80";
        return "";
    }, [status])

    if (bookings.length === 0) {
        return (
            <></>
        );
    }
    return (
        <>
            <div onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                <Button 
                    className={`${butttonClass} min-w-max px-2.5 py-1 text-xs font-medium h-auto`} 
                    onClick={openConfigModal} 
                    loading={isLoading} 
                    disabled={isLoading}
                >
                    {buttonLabel}
                </Button>
            </div>
            <Modal 
                show={!!showModal} 
                onClose={closeModal} 
                title={"Battle Kart Game Configuration"} 
                className='flex flex-col'
                titleClassName='text-track-battle'
            >
                {showModal === "config" && (
                    <div className="flex justify-between pt-5 gap-6 h-[calc(100%-65px)] overflow-hidden">
                        <div className="flex flex-col overflow-hidden flex-1">
                            <div className="flex-1 flex flex-col md:flex-row space-y-6 md:space-y-0 md:space-x-6 pb-4 overflow-hidden">
                                {/* Left Panel */}
                                <div className="flex-1 flex-grow-[3] flex flex-col overflow-y-auto">
                                    <ModalSection title={`${session.option1} Game Details`} titleClassName='text-track-battle'>
                                        <div>
                                            {
                                                ((MAX_GAME_PER_SESSION !== Infinity) && (config.length >= MAX_GAME_PER_SESSION)) ? (
                                                    <p className="text-sm text-red-600 mb-4">You already added {config.length} game modes to your session</p>
                                                ) : (                                                    
                                                    <p className="text-sm text-gray-600 mb-4">Select game modes to add to your session</p>
                                                )
                                            }
                                            <div className="flex gap-2 w-full mt-2 flex-1">
                                                <BattleKartGameCard 
                                                    game={EBattleKartGames.BattleRace} 
                                                    className="flex-1" 
                                                    onClick={() => handleAddMode(EBattleKartGames.BattleRace, remainingTime || 1)} 
                                                />
                                            </div>
                                            <div className="flex gap-2 w-full mt-2 flex-1">
                                                <BattleKartGameCard 
                                                    game={EBattleKartGames.BattleSnake} 
                                                    className="flex-1" 
                                                    onClick={() => handleAddMode(EBattleKartGames.BattleSnake, remainingTime || 1)} 
                                                />
                                                <BattleKartGameCard 
                                                    game={EBattleKartGames.BattleColour} 
                                                    className="flex-1" 
                                                    onClick={() => handleAddMode(EBattleKartGames.BattleColour, remainingTime || 1)} 
                                                />
                                            </div>
                                            <div className="flex gap-2 w-full mt-2 flex-1">
                                                <BattleKartGameCard 
                                                    game={EBattleKartGames.BattleFoot} 
                                                    className="flex-1" 
                                                    onClick={() => handleAddMode(EBattleKartGames.BattleFoot, remainingTime || 1)} 
                                                />
                                                <BattleKartGameCard 
                                                    game={EBattleKartGames.BattleVirus} 
                                                    className="flex-1" 
                                                    onClick={() => handleAddMode(EBattleKartGames.BattleVirus, remainingTime || 1)} 
                                                />
                                            </div>
                                        </div>
                                    </ModalSection>
                                    <ModalSection title='Select Game Modes' className='mt-2 flex-1 flex flex-col' titleClassName='text-track-battle' headerContent={(
                                        <div className="ml-auto">
                                            <Button variant="extraSmall" className="bg-gray-500" onClick={resetConfig}>Reset to Default</Button>
                                        </div>
                                    )}>
                                    <div className='flex-1 flex flex-col'>
                                        <div className='flex-1 flex flex-col gap-4' id="currentSelectedModesContainer">
                                            {config.length === 0 ? (
                                                <div className="p-4 text-center text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
                                                    No game modes added.
                                                </div>
                                            ) : (
                                                config.map((configItem, index) => (
                                                <div 
                                                    key={`${configItem.name}-${index}`}
                                                    className="flex items-center justify-between transition-all duration-200 hover:shadow-md border-2 border-transparent rounded-lg"
                                                    onDragEnter={(e) => handleDragEnter(e, index)}
                                                    onDragLeave={handleDragLeave}
                                                    onDrop={handleDrop}
                                                    onDragOver={handleDragOver}
                                                    data-index={index}
                                                >
                                                    <CustomizeGame 
                                                        game={configItem.name as EBattleKartGames} 
                                                        onClear={() => handleRemoveSelectedMode(index)}
                                                        className="w-full"
                                                        duration={configItem.duration}
                                                        onDurationChange={(newDuration: number) => handleChangeDuration(index, newDuration)}
                                                        index={index}
                                                        onDragStart={handleDragStart}
                                                        onDragEnd={handleDragEnd}
                                                    />
                                                </div>
                                                ))
                                            )}
                                        </div>

                                        <div className={`${totalTime > MAX_SESSION_TIME ? "bg-red-600 text-white" : "bg-gray-50 text-black"} flex justify-center items-center p-2 rounded-md font-semibold text-sm mt-2`}>Total Time: {totalTime}/{MAX_SESSION_TIME} mins</div>
                                    </div>

                                    </ModalSection>
                                </div>

                                {/* Right Panel */}
                                <div className="flex-1 rounded-lg flex flex-col">
                                    {(!!registeredDrivers.length) ? (
                                        <>
                                            <ModalSection title='Registered Drivers' className='overflow-y-auto' titleClassName='text-track-battle' headerContent={(
                                                <div className='flex items-center justify-center ml-auto'>
                                                    <div className='text-sm text-gray-500'>({totalNumOfDriversRegistered}/{totalNumOfDrivers})</div>
                                                </div>
                                            )}>
                                                <div>
                                                    {/* Sort Filter Container */}
                                                    <div className="flex flex-col lg:flex-row items-center justify-end mb-4 gap-2">
                                                        <TextInput placeholder="Search drivers in this game" value={searchQuery} onChange={setSearchQuery} />
                                                        <select
                                                            id="sortDriversSelect"
                                                            className="flex-1 p-3 rounded-lg border border-solid border-[#C2C0C3] text-[#393B41] text-xs not-italic font-medium leading-[normal] font-[Montserrat] w-full lg:w-auto"
                                                            value={sortOrder}
                                                            onChange={handleSortDriversChange}
                                                        >
                                                            <option value="">Sort by Name</option>
                                                            <option value="asc">Ascending</option>
                                                            <option value="desc">Descending</option>
                                                        </select>
                                                    </div>

                                                    {/* Registered Drivers List Container */}
                                                    <div className="flex-1 overflow-y-auto pr-2 -mr-2">
                                                        <ul id="registeredDriversList" className="space-y-3">
                                                            {filteredRegisteredDrivers.length === 0 ? (
                                                                <li className="p-3 text-gray-600">No drivers found.</li>
                                                            ) : (
                                                                filteredRegisteredDrivers.map(driver => (
                                                                    <li data-order-id={driver.orderId} data-driver-id={driver.driver.firebase_id} key={`${driver.orderId}-${driver.driver.firebase_id}`} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg shadow-sm border border-gray-200">
                                                                        <div className="flex items-center flex-grow">
                                                                            <div className='flex flex-col'>
                                                                                <span className={`text-gray-700 font-medium ${isDriverCleared(driver.orderId, driver.driver.firebase_id) ? "line-through" : ""}`}>{driver.driver.first_name} {driver.driver.last_name}</span>
                                                                                <span className="text-sm text-gray-500">{driver.driver.dob ? `${new Date().getFullYear() - new Date(driver.driver.dob).getFullYear()} years old` : "N/A"}</span>
                                                                            </div>
                                                                            <div className="ml-auto flex gap-2 items-center">
                                                                                <div className='cursor-pointer' onClick={handleViewDriverClick(driver)}>
                                                                                    <EyeIcon className="w-4 h-4" />
                                                                                </div>
                                                                                {isDriverCleared(driver.orderId, driver.driver.firebase_id) ? (
                                                                                    <div className='w-5 h-5 bg-green-500 rounded-full flex items-center justify-center cursor-pointer' onClick={() => addDriver(driver.orderId, driver.driver.firebase_id)}>
                                                                                        <CheckIcon className='w-3 h-3 text-white' />
                                                                                    </div>
                                                                                ) : (
                                                                                    <div className='w-5 h-5 bg-red-500 rounded-full flex items-center justify-center cursor-pointer' onClick={() => clearDriver(driver.orderId, driver.driver.firebase_id)}>
                                                                                        <XIcon className='w-3 h-3 text-white' />
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </li>
                                                                ))
                                                            )}
                                                        </ul>
                                                        <div className="w-full flex flex-col justify-center mt-2">
                                                            <ul className="my-2">
                                                                {customPlayers.map((player, index) => (
                                                                    <li key={index} className="flex items-center justify-between p-3 bg-gray-200 rounded-lg shadow-sm border border-gray-200">
                                                                        <div className="flex items-center justify-between flex-grow">
                                                                            <span className="text-gray-700 font-medium">{player.nickname}</span>
                                                                            <div className='w-5 h-5 bg-red-500 rounded-full flex items-center justify-center cursor-pointer' onClick={() => removeCustomPlayer(index)}>
                                                                                <XIcon className='w-3 h-3 text-white' />
                                                                            </div>
                                                                        </div>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                            <AddCustomPlayer onAddPlayer={handleAddCustomPlayer} />
                                                        </div>
                                                    </div>
                                                </div>
                                            </ModalSection>
                                            {totalNumOfDriversRegistered > MAX_NUMBER_OF_PLAYERS && <p className="text-xs text-red-600 mt-2 font-bold">More than {MAX_NUMBER_OF_PLAYERS} players added. Please adjust to start the game</p>}
                                        </>
                                    ): (
                                        <ModalSection title='Registered Drivers' titleClassName='text-track-battle'>
                                            <div>
                                                <p className="text-sm text-gray-600 mb-4">No drivers found.</p>
                                            </div>
                                        </ModalSection>
                                    )}
                                </div>
                            </div>

                            {/* Bottom Section: Confirmation and Button */}
                            {(status === "not_started") && (
                                <div className="flex-none flex items-center justify-end space-x-4 pt-4 border-t border-gray-200">
                                    <p className="text-gray-700 font-medium text-lg">Are you sure all the details are correct?</p>
                                    <Button className='bg-green-500 hover:bg-green-600 disabled:bg-track-battle/50' disabled={isConfirmButtonDisabled} onClick={onSubmit} loading={isLoading} >Confirm & Start Game</Button>
                                </div>
                            )}
                            {(["in_progress", "completed"].includes(status)) && (
                                <div className="flex-none flex items-center justify-end space-x-4 pt-4 border-t border-gray-200">
                                    <p className="text-gray-700 font-medium text-lg">{status === "in_progress" ? "Game is already started." : "Game is finished."}</p>
                                    <Button className='bg-red-500 hover:bg-red-600 disabled:bg-red-300' disabled={isConfirmButtonDisabled} onClick={onSubmit} loading={isLoading} >Restart Game</Button>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </Modal>
        </>
    )
}

const BattleKartGameCard = ({ game, className, disabled, onClick }: { game: EBattleKartGames, className?: string, disabled?: boolean, onClick?: () => void }) => {
    const gradient = BATTLE_KART_GAME_GRADIENTS[game];
    return (
        <div 
            className={`rounded-lg p-3 flex items-center justify-between ${onClick ? "cursor-pointer" : ""} ${className} ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
            style={{
                background: `linear-gradient(to left, ${gradient.gradientStart}, ${gradient.gradientEnd})`
            }}
            onClick={disabled ? undefined : onClick}
        >
            <img src={BattleKartGameIconWithText[game]} alt={game} className='h-8 w-auto' />
            {/* <div className="text-white text-sm font-bold">{game}</div> */}
            <div className={`w-5 h-5 bg-white bg-opacity-20 rounded-full flex items-center justify-center backdrop-blur-sm`}>
                <PlusIcon className='w-3 h-3 text-white' />
            </div>
        </div>
    )
}

const CustomizeGame = ({ 
    game, 
    className, 
    onClear, 
    duration = 5, 
    onDurationChange,
    index,
    onDragStart,
    onDragEnd
}: { 
    game: EBattleKartGames, 
    className?: string, 
    onClear?: () => void,
    duration?: number,
    onDurationChange?: (newDuration: number) => void,
    index?: number,
    onDragStart?: (e: React.DragEvent<HTMLDivElement>, position: number) => void,
    onDragEnd?: (e: React.DragEvent<HTMLDivElement>) => void
}) => {
    const gradient = BATTLE_KART_GAME_GRADIENTS[game];
    const containerRef = useRef<HTMLDivElement>(null);

    const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
        // Create a drag image with reduced opacity
        if (containerRef.current) {
            // Clone the container element
            const dragImage = containerRef.current.cloneNode(true) as HTMLElement;
            
            // Apply reduced opacity and positioning
            dragImage.style.opacity = '0.6';
            dragImage.style.position = 'absolute';
            dragImage.style.top = '-1000px';
            dragImage.style.left = '-1000px';
            dragImage.style.pointerEvents = 'none';
            
            // Temporarily add to document
            document.body.appendChild(dragImage);
            
            // Set as drag image
            e.dataTransfer.setDragImage(dragImage, 0, 0);
            
            // Clean up after drag starts
            setTimeout(() => {
                document.body.removeChild(dragImage);
            }, 0);
        }
        // Call the parent's drag start handler
        if (onDragStart && index !== undefined) {
            onDragStart(e, index);
        }
    };

    return (
        <div 
            ref={containerRef}
            className={`rounded-lg p-2 w-full flex items-center justify-between gap-2 relative ${className}`}
            style={{
                background: `linear-gradient(to right, ${gradient.gradientStart}, ${gradient.gradientEnd})`
            }}
        >
            <div 
                className='text-white font-bold cursor-grab flex items-center gap-1 p-1'
                draggable
                onDragStart={handleDragStart}
                onDragEnd={onDragEnd}
            >
                ⋮⋮
            </div>
            <img src={BattleKartGameIconWithText[game]} alt={game} className='h-8 w-auto' />
            {/* <div className="text-sm text-white font-semibold">{game}</div> */}
            <InputSlider 
                MIN={1}
                MAX={MAX_SESSION_TIME}
                STEP={1}
                className='flex-1'
                activeTrackColor="rgba(255, 255, 255, 0.5)"
                inactiveTrackColor="rgba(255, 255, 255, 0.2)"
                thumbColor="#fff"
                tooltipColor={gradient.gradientStart}
                value={duration}
                onChange={onDurationChange}
            />
            <div className='text-sm font-medium bg-white text-black rounded-2xl px-2 py-1'>{duration} mins</div>
            <div className='absolute top-[-5px] right-[-5px] w-5 h-5 bg-red-500 rounded-full flex items-center justify-center cursor-pointer' onClick={onClear}>
                <XIcon className='w-3 h-3 text-white' />
            </div>
        </div>
    )
}

const AddCustomPlayer = ({ onAddPlayer }: { onAddPlayer: (newPlayer: IAddedPlayer) => void }) => {
    const [nickname, setNickname] = useState("");
    const [showCustomPlayerInput, setShowCustomPlayerInput] = useState(false);

    const onSubmit = useCallback(() => {
        onAddPlayer({ nickname });
        setShowCustomPlayerInput(false);
        setNickname("");
    }, [nickname, onAddPlayer]);

    const onCancel = useCallback(() => {
        setShowCustomPlayerInput(false);
        setNickname("");
    }, []);

    return (
        <>
            {showCustomPlayerInput ? (
                <div className="flex items-center gap-2">
                    <TextInput
                        placeholder="Enter nickname"
                        value={nickname}
                        onChange={setNickname}
                    />
                    <Button variant="extraSmall" className="bg-track-battle/80" onClick={onSubmit}><PlusSmIcon className="text-white h-4" /></Button>
                    <Button variant="extraSmall" className="bg-red-500" onClick={onCancel}><XIcon className="text-white h-4" /></Button>
                </div>
            ) : (
                <div className='flex items-center justify-center'>
                    <Button variant="extraSmall" className="bg-track-battle/80" onClick={()=>setShowCustomPlayerInput(true)}>Quick Add Player</Button>
                </div>
            )}
        </>
    )
}

const MAX_SESSION_TIME = 10;
const MAX_NUMBER_OF_PLAYERS = 12;

const draggingItemClasses = {
    normal: ["border-transparent"],
    hover: ["border-blue-500", "bg-blue-50", "border-2"],
    dragging: ["opacity-50", "scale-95"],
}

const BATTLE_KART_GAME_GRADIENTS = {
    [EBattleKartGames.BattleRace]: {
      "gradientStart": "#E63729",
      "gradientEnd": "#FFE00B"
    },
    [EBattleKartGames.BattleFoot]: {
      "gradientStart": "#0178B0",
      "gradientEnd": "#6BC8F2"
    },
    [EBattleKartGames.BattleColour]: {
      "gradientStart": "#A81C50",
      "gradientEnd": "#EE7A9D"
    },
    [EBattleKartGames.BattleSnake]: {
      "gradientStart": "#E1B509",
      "gradientEnd": "#FFFAD7"
    },
    [EBattleKartGames.BattleVirus]: {
      "gradientStart": "#01a17f",
      "gradientEnd": "#d9e211"
    }
}

export default StartBattleKartButton